import {ApiKeysResponse} from "./admin";

const getCookieValue = (name: string) => {
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
};

export type TokenResponse = {
    access_token: string
    refresh_token?: string
    expires_in: number
};

export class APIError {
    error: string;

    constructor(error: string) {
        this.error = error;
    }

    toString() {
        return this.error;
    }
}

export class APIResponseError extends APIError {
    response: Response;

    constructor(error: string, response: Response) {
        super(error);
        this.response = response;
    }
}

export class API {
    base: URL;
    clientId: string;
    refreshTokenUrl: URL;

    constructor(clientId: string, refreshTokenUrl: URL | string, base: URL | string) {
        this.base = (base instanceof URL) ? base : new URL(base, location.href);
        this.clientId = clientId;
        this.refreshTokenUrl = new URL(refreshTokenUrl, this.base);
    }

    get refreshToken(): string {
        return getCookieValue("refresh_token");
    }

    set refreshToken(token: string) {
        document.cookie = `refresh_token=${encodeURIComponent(token)}; path=/; max-age=${86400 * 365}`;
    }

    get accessToken(): string {
        return getCookieValue("access_token");
    }

    set accessToken(token: string) {
        document.cookie = `access_token=${encodeURIComponent(token)}; path=/`;
    }

    refreshAccessToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            // Try to obtain new token from refresh token, and retry the query.
            const refreshToken = this.refreshToken;
            if (refreshToken) {
                const fd = new FormData();
                fd.append("client_id", this.clientId);
                fd.append("grant_type", "refresh_token");
                fd.append("refresh_token", refreshToken);

                fetch(this.refreshTokenUrl, {
                    method: "POST",
                    body: fd
                }).then(token => {
                    if (token.status !== 200) {
                        reject(token.toString());
                    } else {
                        return token.json();
                    }
                }).then((token: TokenResponse) => {
                    this.accessToken = token.access_token;

                    if (token.refresh_token) {
                        this.refreshToken = token.refresh_token;
                    }

                    resolve(token.access_token);
                }).catch(e => reject(e.toString()));
            } else {
                reject("Unable to refresh access token, due to missing refresh token.");
            }
        });
    }

    async fetch(path: string | URL, opts?: RequestInit): Promise<Response> {
        opts = opts || {};

        const hdrs = new Headers(opts.headers);

        if (this.accessToken) {
            hdrs.append("Authorization", `Bearer ${this.accessToken}`);
        }

        opts = Object.assign(opts, {"headers": hdrs});

        if (!(path instanceof URL)) {
            path = new URL(path, this.base);
        }

        return (new Promise<Response>((resolve, reject) => {
            fetch(path, opts).then((resp) => {
                // Try to renew access token from refresh token.
                if (resp.status === 401) {
                    console.info(`API call to ${path} failed with 401, trying to get new access token...`);
                    this.refreshAccessToken().then((accessToken) => {
                        console.info(`Got new access token, retrying call to ${path}...`);

                        // Retry the request with new access token, this time without additional processing.
                        hdrs.set("Authorization", `Bearer ${accessToken}`);
                        fetch(path, opts).then(resolve, reject);
                    }).catch((e) => {
                        // Resolve with original response from the first API call (Unauthorized).
                        resolve(resp);
                    });
                } else {
                    resolve(resp);
                }
            }, reject);
        })).catch(err => {
            if (err instanceof APIError) {
                throw err;
            } else {
                throw new APIError(`Error retrieving data: ${err.message}`);
            }
        });
    }

    async fetchJSON<T>(path: string | URL, opts?: RequestInit): Promise<T> {
        const resp = await this.fetch(path, opts);
        if (resp.status == 200) {
            return await resp.json();
        } else {
            throw new APIResponseError(`Error retrieving data: ${resp.statusText}`, resp);
        }
    }

    async postJSON<Resp, Req = any>(path: string | URL, payload: Req, method: string = "POST"): Promise<Resp> {
        return await this.fetchJSON<Resp>(path, {
            method: method,
            body: JSON.stringify(payload),
            headers: {
                "Content-Type": "application/json"
            }
        });
    }
}
